import { gql } from "@apollo/client";
import { BREAKDOWN, CHECKIN_REQUIREMENTS_FRAGMENT, FEES_FOR_PAX } from "./fragments";

export const GET_CURRENT_BOOKING_STATE_CHECKIN = gql`
  ${FEES_FOR_PAX}
  ${BREAKDOWN}
  query booking {
    booking {
      info {
        status
      }
      bookingKey
      selfServiceMoveAvailable
      recordLocator
      currencyCode
      typeOfSale {
        promotionCode
      }
      queues {
        code
        name
        type
        flightReference
      }
      sales {
        created {
          organizationCode
        }
      }
      contacts {
        value {
          emailAddress
          name {
            ...name
          }
          phoneNumbers {
            number
            type
          }
          contactTypeCode
          companyName
        }
      }
      journeys {
        flightType
        designator {
          origin
          destination
          arrival
          departure
        }
        journeyKey
        segments {
          segmentKey
          identifier {
            carrierCode
            identifier
            opSuffix
          }
          status
          designator {
            origin
            destination
            arrival
            departure
          }
          segmentKey
          identifier {
            identifier
            carrierCode
            opSuffix
          }
          passengerSegment {
            value {
              liftStatus
              passengerKey

              seats {
                arrivalStation
                compartmentDesignator
                departureStation
                passengerKey
                seatInformation {
                  propertyList {
                    key
                    value
                  }
                }
                unitDesignator
                unitKey
              }
              ssrs {
                count
                feeCode
                passengerKey
                ssrCode
                ssrKey
                ssrNumber
                inBundle
                market {
                  departureDate
                  destination
                  origin
                }
              }
            }
          }
          cabinOfService
          fares {
            fareKey
            productClass
            classOfService
            passengerFares {
              passengerType
              serviceCharges {
                amount
                code
                currencyCode
                detail
                type
              }
            }
          }
          flightReference
          international
          isStandby
          legs {
            designator {
              origin
              destination
              arrival
              departure
            }
            legKey
            legInfo {
              arrivalTimeUtc
              departureTimeUtc
              arrivalTerminal
              departureTerminal
            }
            operationsInfo {
              departureTimes {
                estimated
                scheduled
              }
              arrivalGate {
                actualGate
                estimatedGate
              }
              departureGate {
                actualGate
                estimatedGate
              }
            }
            seatmapReference
          }
        }
        stops
      }
      passengers {
        key

        value {
          name {
            ...name
          }

          passengerTypeCode
          passengerAlternateKey
          info {
            dateOfBirth
            familyNumber
            gender
            nationality
            residentCountry
          }
          addresses {
            city
            companyName
            countryCode
            emailAddress
            lineOne
            lineTwo
            lineThree
            passengerAddressKey
            phone
            postalCode
            provinceState
            stationCode
            status
          }
          customerNumber
          discountCode
          infant {
            dateOfBirth
            gender
            name {
              ...name
            }
            nationality
            residentCountry
            travelDocuments {
              passengerTravelDocumentKey
              birthCountry
              documentTypeCode
              expirationDate
              issuedByCode
              issuedDate
              name {
                ...name
              }
              nationality
              number
            }
          }
          travelDocuments {
            passengerTravelDocumentKey
            birthCountry
            documentTypeCode
            expirationDate
            issuedByCode
            issuedDate
            name {
              ...name
            }
            nationality
            number
          }
          program {
            code
            levelCode
            number
          }
        }
      }
      breakdown {
        ...breakdown
      }
      seats: journeys {
        ...seats
      }
      passengerSegments: journeys {
        ...passengerSegments
      }
      fees: passengers {
        ...fees
      }
      passengerFaresReference: journeys {
        journeyKey
        segments {
          fares {
            passengerFares {
              passengerType
              serviceCharges {
                amount
                code
                currencyCode
                detail
                type
              }
            }
          }
          segmentKey
          flightReference
          legs {
            legKey
            flightReference
          }
        }
      }
      payments {
        paymentKey
        amounts {
          amount
          collected
        }
        details {
          accountNumber
          installments
          parentPaymentId
          accountName
          expirationDate
          fields {
            key
            value
          }
        }
        voucher {
          overrideAmount
        }
        status
        authorizationStatus
        code
        createdDate
      }
    }
  }
  # Name
  fragment name on Name {
    first
    last
    middle
    title
    suffix
  }

  fragment charge on ChargeBreakdown {
    charges {
      amount
      collectType
      code
      currencyCode
      detail
      type
    }
    taxes
    total
  }

  fragment passengerSegments on Journey {
    journeyKey
    segments {
      segmentKey
      passengerSegment {
        passengerKey: key

        value {
          liftStatus
          hasInfant
          seats {
            arrivalStation
            compartmentDesignator
            departureStation
            passengerKey
            seatInformation {
              propertyList {
                key
                value
              }
            }
            unitDesignator
            unitKey
          }
          ssrs {
            count
            isConfirmed
            feeCode
            passengerKey
            ssrCode
            ssrKey
            ssrNumber
            inBundle
            market {
              departureDate
              destination
              origin
            }
          }
        }
      }
    }
  }
  fragment seats on Journey {
    segments {
      segmentKey
      passengerSegment {
        passengerKey: key
        value {
          seats {
            arrivalStation
            compartmentDesignator
            departureStation
            passengerKey
            penalty
            seatInformation {
              propertyList {
                key
                value
              }
            }
            unitDesignator
            unitKey
          }
        }
      }
    }
  }
`;

export const CHECKIN_RETRIEVE = gql`
  query ($request: Input_RetrieveBookingv2) {
    booking: bookingRetrievev2(request: $request) {
      info {
        status
      }
      selfServiceMoveAvailable
      recordLocator
      currencyCode
    }
  }
`;

export const CHECKIN_REQUIREMENTS = gql`
  # Check if any of the segment within the journey can be checked in
  ${CHECKIN_REQUIREMENTS_FRAGMENT}
  query ($journeyKey: String) {
    checkinByJourneyRequirements(journeyKey: $journeyKey) {
      ...checkinRequirements
    }
    booking {
      passengerSegments: journeys {
        journeyKey
        segments {
          segmentKey
          passengerSegment {
            passengerKey: key
            value {
              liftStatus
              hasInfant
            }
          }
        }
      }
    }
  }
`;

export const CHECKIN_REQUIREMENTS_SEGMENT_LEVEL = gql`
  # Check if any of the segment within the journey can be checked in
  ${CHECKIN_REQUIREMENTS_FRAGMENT}
  query ($segKey: String) {
    checkinBySegmentRequirements(segmentKey: $segKey) {
      ...checkinRequirements
    }
    booking {
      passengerSegments: journeys {
        journeyKey
        segments {
          segmentKey
          passengerSegment {
            passengerKey: key
            value {
              liftStatus
              hasInfant
            }
          }
        }
      }
    }
  }
`;
export const ORIGINANDDESTINATION = gql`
  query MasterData(
    $cultureCode: String
    $stationsEtag: String
    $fareTypesEtag: String
    $passengerTypesEtag: String
  ) {
    stations: resourceStations(query: { activeOnly: true, cultureCode: $cultureCode, eTag: $stationsEtag }) {
      values {
        fullName
        locationDetails {
          cityCode
        }
        shortName
        fullName
        stationCode
      }
    }
    fareTypes: resourceFareTypes(query: { activeOnly: true, cultureCode: $cultureCode, eTag: $fareTypesEtag }) {
      eTag
      expiration
      values {
        allowed
        fareTypeCode
        inActive
        name
      }
    }
    passengerTypes: resourcePassengerTypes(
      query: { activeOnly: true, cultureCode: $cultureCode, eTag: $passengerTypesEtag }
    ) {
      values {
        allowed
        inActive
        maximumAge
        minimumAge
        name
        nonAdult
        passengerTypeCode
        typeIndicator
      }
    }
    cities: resourceCities(query: { activeOnly: true }) {
      expiration
      values {
        cityCode
        provinceStateCode
        countryCode
        name
        shortName
      }
    }
  }
`;
